import { round } from 'lodash';

import { SalesType } from '@client/new/common/types';
import { ArrayItem, SkipNull } from '@client/new/helpers/types';
import {
  AccessType,
  BundleItemType,
  BundlePricing,
  BundleStatus,
  ProgramBusinessModel,
  ProgramsGetListQuery,
} from 'generated/graphql';

import {
  CreatorMode,
  ProgramBundle,
  ProgramBundleStatus,
  ProgramBusinessModelKind,
  ProgramItem,
  ProgramMediaCover,
  ProgramMetrics,
  ProgramOffer,
  ProgramOfferState,
  ProgramsCreator,
  ProgramsDomain,
  ProgramStateKind,
  ProgramStatus,
  SubscriptionSublabel,
} from './programs.types';

// ---------------------------------------------------------------------------
// Raw server aliases
// ---------------------------------------------------------------------------

export type RawProgram = SkipNull<
  ArrayItem<ProgramsGetListQuery['collections']>
>;

export type RawWorkout = SkipNull<ArrayItem<SkipNull<RawProgram['workouts']>>>;

export type RawBundle = SkipNull<ArrayItem<ProgramsGetListQuery['bundle']>>;

export type RawCreator = SkipNull<ProgramsGetListQuery['creator']>;

// ---------------------------------------------------------------------------
// Individual pure helpers
// ---------------------------------------------------------------------------

/**
 * Translates the generated `ProgramBusinessModel` enum into the
 * domain-side string union so downstream layers never import the
 * generated GraphQL types.
 */
export const toProgramBusinessModelKind = (
  raw: ProgramBusinessModel | null | undefined,
): ProgramBusinessModelKind | null => {
  if (raw === ProgramBusinessModel.Subscription) return 'subscription';
  if (raw === ProgramBusinessModel.Purchase) return 'purchase';
  if (raw === ProgramBusinessModel.Unassigned) return 'unassigned';
  return null;
};

/**
 * Translates the generated `BundleStatus` enum to the domain string union.
 */
export const toProgramBundleStatus = (
  raw: BundleStatus | null | undefined,
): ProgramBundleStatus => {
  if (raw === BundleStatus.Published) return 'PUBLISHED';
  if (raw === BundleStatus.Archived) return 'ARCHIVED';
  return 'DRAFT';
};

/**
 * Derives the wire-value program status from server-side publish/archive
 * timestamps. This is the raw projection of backend state — not the
 * creator-facing lifecycle (see `deriveProgramState`).
 *
 *  - `archivedAt` set AFTER `publishedAt` means the program was published
 *    once, then pulled back → `archived`.
 *  - `publishedAt` set and no archive → `published`.
 *  - Neither timestamp → `draft`.
 *  - `archivedAt` set without `publishedAt` is treated as `draft` (the
 *    program never went live, so there is nothing to archive).
 */
export const toProgramStatus = (p: {
  publishedAt?: string | null;
  archivedAt?: string | null;
}): ProgramStatus => {
  if (p.archivedAt) return 'archived';
  if (p.publishedAt) return 'published';
  return 'draft';
};

/**
 * Derives the creator-facing four-state lifecycle from the mapped
 * `ProgramItem`-shaped inputs: wire `status` + `offerState` + business
 * model + bundle membership.
 *
 * Rules (match `docs/brainstorms/2026-04-22-program-offer-business-logic-requirements.md`):
 *  - `status === 'archived'` → `'archived'` (terminal).
 *  - `status === 'draft'`    → `'draft'`.
 *  - Legacy creators (no bundle/offer abstraction): `published-wire +
 *    isVisible` → `'published'`; otherwise `'ready'`.
 *  - `status === 'published'` AND at least one PUBLISHED offer contains
 *    the program → `'published'`. For Subscription business model this
 *    means at least one live subscription bundle with the program; for
 *    OTP it means at least one `isActive: true` OTP offer in `offerState`.
 *  - Otherwise (published-wire but no live offer) → `'ready'`.
 *
 * Inputs are the same ones `toNotVisibleToConsumers` consumes, kept in
 * parallel to stay consistent with its semantics.
 */
export const deriveProgramState = (params: {
  status: ProgramStatus;
  programBusinessModel: ProgramBusinessModel | null;
  offerState: ProgramOfferState;
  programId: number;
  bundles: ReadonlyArray<RawBundle>;
  creatorMode: CreatorMode;
  isVisible: boolean;
}): ProgramStateKind => {
  if (params.status === 'archived') return 'archived';
  if (params.status === 'draft') return 'draft';
  // Legacy creators don't have the bundle/offer abstraction the unified
  // page uses for "Ready" vs "Published" — they publish straight to the
  // consumer, so visibility alone is the discriminator.
  if (params.creatorMode === 'legacy') {
    return params.isVisible ? 'published' : 'ready';
  }
  // status === 'published' (wire): distinguish READY vs PUBLISHED by
  // whether a live offer actually contains the program.
  if (params.programBusinessModel === ProgramBusinessModel.Subscription) {
    return isLiveSubscriptionBundleWithProgram(params.programId, params.bundles)
      ? 'published'
      : 'ready';
  }
  return hasLiveOtpOffer(params.offerState) ? 'published' : 'ready';
};

/**
 * Derives the Offers-column shape for a specific program by combining its
 * business model with the subset of creator bundles that actually include
 * the program as an item.
 *
 * Invariants:
 *  - `Subscription` short-circuits to `{type: 'subscription'}` regardless of
 *    bundles (subscription programs are exclusive by backend contract).
 *  - For everything else, only OTP bundles whose `items` contain
 *    `{itemType: PROGRAM, itemId: programId}` count as membership. Bundles
 *    without `items` (legacy payloads) contribute nothing.
 *  - An empty result maps to `{type: 'none'}` so the UI shows the add CTA.
 */
const toSubscriptionSublabel = (
  programId: number,
  bundles: ReadonlyArray<RawBundle>,
): SubscriptionSublabel => {
  const matchingBundles = bundles.filter(
    (b) =>
      b.pricing === BundlePricing.Subscription &&
      bundleContainsProgram(b, programId),
  );

  const hasExclusive = matchingBundles.some((b) => !b.isPlatformBundle);
  const hasPlatform = matchingBundles.some((b) => b.isPlatformBundle);

  if (hasExclusive && hasPlatform) return 'exclusive-and-platform';
  if (hasPlatform) return 'platform';
  if (hasExclusive) return 'exclusive';
  return null;
};

export const toProgramOfferState = (
  programId: number,
  businessModel: ProgramBusinessModel | null | undefined,
  bundles: ReadonlyArray<RawBundle>,
): ProgramOfferState => {
  if (businessModel === ProgramBusinessModel.Subscription) {
    return {
      type: 'subscription',
      sublabel: toSubscriptionSublabel(programId, bundles),
    };
  }

  const visibleOffers: Array<ProgramOffer> = bundles
    .filter(isOtpBundle)
    .filter(isVisibleOtpBundle)
    .filter((b) => bundleContainsProgram(b, programId))
    .map(toProgramOffer)
    .sort((a, b) => a.id - b.id);

  if (visibleOffers.length === 0) return { type: 'none' };
  return { type: 'otp', offers: visibleOffers };
};

// OTP bundles only; subscription bundles are handled by the program's
// business model, not by the bundle items list.
const isOtpBundle = (b: RawBundle): boolean =>
  // `pricing` may be absent in legacy payloads — fall back to "looks OTP" so
  // older cached responses still render sensibly.
  b.pricing === undefined || b.pricing === null
    ? true
    : b.pricing === BundlePricing.Otp;

// Draft OTP bundles should still appear in the Offers column, but archived
// bundles remain hidden.
const isVisibleOtpBundle = (b: RawBundle): boolean =>
  b.status === BundleStatus.Published || b.status === BundleStatus.Draft;

// A bundle counts as live only when it is Published.
const isLiveBundle = (b: RawBundle): boolean =>
  b.status === BundleStatus.Published;

const bundleContainsProgram = (b: RawBundle, programId: number): boolean => {
  const items = b.items ?? [];
  return items.some(
    (item) =>
      item &&
      item.itemType === BundleItemType.Program &&
      item.itemId === programId,
  );
};

const toProgramOffer = (b: RawBundle): ProgramOffer => ({
  id: b.id,
  name: b.title,
  isActive: isLiveBundle(b),
  status: toProgramBundleStatus(b.status),
});

const hasLiveOtpOffer = (offerState: ProgramOfferState): boolean =>
  offerState.type === 'otp' &&
  offerState.offers.some((offer) => offer.isActive);

/**
 * Aggregates program-level metrics. `avgRating` comes straight from the
 * server-provided `programStats`. The other three are summed across
 * workouts (mirrors `toCollectionStats` in `collectionMappers.ts`).
 *
 * Nullability semantics:
 *  - Empty/missing `workouts` → `null` for begins/completions/views.
 *  - `rating` is the unweighted mean of each workout's `statsRatingAverage`,
 *    matching the legacy collections page; `null` when no workout has ratings.
 *  - Individual null workout metrics count as 0 in the sum, but the final
 *    result is `null` when ALL workouts are null for that metric.
 */
export const toProgramMetrics = (
  p: Pick<RawProgram, 'workouts' | 'programStats'>,
): ProgramMetrics => {
  const workouts = (p.workouts || []).filter((w): w is RawWorkout =>
    Boolean(w),
  );

  return {
    rating: averageWorkoutRatings(workouts),
    begins: sumNullable(workouts, (w) => w.statsBegins),
    completions: sumNullable(workouts, (w) => w.statsCompletions),
    views: p.programStats ? p.programStats.statsViews : null,
  };
};

const averageWorkoutRatings = (
  workouts: ReadonlyArray<RawWorkout>,
): number | null => {
  const ratings = workouts
    .map((w) => w.statsRatingAverage)
    .filter((r): r is number => typeof r === 'number' && r > 0);
  if (ratings.length === 0) return null;
  return ratings.reduce((acc, r) => acc + r, 0) / ratings.length;
};

const sumNullable = <T>(
  items: ReadonlyArray<T>,
  getValue: (item: T) => number | null | undefined,
): number | null => {
  if (items.length === 0) return null;
  const defined = items
    .map(getValue)
    .filter((v): v is number => v !== null && v !== undefined);
  if (defined.length === 0) return null;
  return defined.reduce((acc, v) => acc + v, 0);
};

export const toCommentsCount = (p: Pick<RawProgram, 'workouts'>): number => {
  const workouts = (p.workouts || []).filter((w): w is RawWorkout =>
    Boolean(w),
  );
  return workouts.reduce((acc, w) => acc + (w.statsComments || 0), 0);
};

/**
 * Human-readable program "subinfo" string: structure label + workout count.
 * Matches the extras list's secondary row (e.g. "Week/Day • 12 workouts").
 */
export const toSubInfo = (
  p: Pick<RawProgram, 'workouts' | 'contentStructure'>,
): string => {
  const count = (p.workouts || []).filter(Boolean).length;
  const structure = toStructureLabel(p.contentStructure);
  const countLabel = `${count} ${count === 1 ? 'workout' : 'workouts'}`;
  return structure ? `${structure} • ${countLabel}` : countLabel;
};

const toStructureLabel = (
  structure: string | null | undefined,
): string | null => {
  if (!structure) return null;
  switch (structure.toUpperCase()) {
    case 'WEEK_DAY':
    case 'DAYWEEK':
      return 'Week/Day';
    case 'SERIES':
    case 'SEQUENTIAL':
      return 'Series';
    case 'MODULE':
      return 'Module';
    default:
      // Fallback: prettify the enum-like value ("ON_DEMAND" → "On Demand").
      return structure
        .toLowerCase()
        .split(/[_\s]+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
};

/**
 * Derives the Model's `salesType` from the program's business model and
 * backend accessType. Subscription programs are always `'subscription'`.
 * Everything else (Purchase / Unassigned) is `'otp'` — the legacy reorder
 * mutation sorts OTP and subscription programs into separate buckets, so
 * this is also the bucket the Model uses when applying an optimistic
 * reorder overlay.
 */
export const toSalesType = (
  businessModel: ProgramBusinessModel | null | undefined,
  accessType: string | null | undefined,
): SalesType => {
  if (businessModel === ProgramBusinessModel.Subscription)
    return 'subscription';
  if (accessType === AccessType.Premium) return 'subscription';
  return 'otp';
};

export const toMediaCover = (
  media: RawProgram['mediaCover'],
): ProgramMediaCover | null => {
  if (!media) return null;
  const cdn01 = media.cdn01 ?? '';
  const image = media.image ?? '';
  if (!cdn01 && !image) return null;
  return { cdn01, image };
};

type RawChallenges = RawProgram['challenges'];

export const toHasChallenges = (challenges: RawChallenges): boolean =>
  (challenges || []).filter(Boolean).length > 0;

export const toHasOngoingChallenges = (challenges: RawChallenges): boolean =>
  (challenges || []).some((ch) => {
    if (!ch) return false;
    const end = new Date(ch.completionEnd);
    return end.getTime() > Date.now();
  });

const isLiveSubscriptionBundleWithProgram = (
  programId: number,
  bundles: ReadonlyArray<RawBundle>,
): boolean =>
  bundles.some(
    (b) =>
      b.status === BundleStatus.Published &&
      b.pricing === BundlePricing.Subscription &&
      bundleContainsProgram(b, programId),
  );

/**
 * True when the program cannot be reached by consumers in the app. See
 * `ProgramItem.notVisibleToConsumers` for the full semantics.
 *
 * Subscription programs always map to `offerState: { type: 'subscription' }`
 * regardless of bundle membership (invariant of `toProgramOfferState`), so
 * `offerState` cannot answer the visibility question for them -- bundle
 * membership is checked explicitly. For every other business model
 * (`OTP`, `Exclusive`, `Unassigned`), visibility depends on whether the row
 * has at least one live OTP offer; draft-only offer membership is still not
 * consumer-visible.
 */
export const toNotVisibleToConsumers = (params: {
  status: ProgramStatus;
  programBusinessModel: ProgramBusinessModel | null;
  offerState: ProgramOfferState;
  programId: number;
  bundles: ReadonlyArray<RawBundle>;
  creatorMode: CreatorMode;
  isVisible: boolean;
}): boolean => {
  if (params.status !== 'published') return true;
  // Legacy creators publish straight to the consumer — `isVisible` alone
  // decides whether the program is reachable, the bundle/offer abstraction
  // does not apply.
  if (params.creatorMode === 'legacy') {
    return !params.isVisible;
  }
  if (params.programBusinessModel === ProgramBusinessModel.Subscription) {
    return !isLiveSubscriptionBundleWithProgram(
      params.programId,
      params.bundles,
    );
  }
  return !hasLiveOtpOffer(params.offerState);
};

/**
 * Computes the rounded average rating the UI should display, matching the
 * one-decimal format used by the collections list.
 */
export const toRoundedRating = (avg: number | null): number | null =>
  avg === null ? null : round(avg, 1);

// ---------------------------------------------------------------------------
// Composite glue
// ---------------------------------------------------------------------------

export const toProgramItem = (
  raw: RawProgram,
  bundles: ReadonlyArray<RawBundle>,
  creatorMode: CreatorMode,
): ProgramItem => {
  const offerState = toProgramOfferState(
    raw.id,
    raw.programBusinessModel,
    bundles,
  );
  const metrics = toProgramMetrics(raw);
  const status = toProgramStatus({
    publishedAt: raw.publishedAt ?? null,
    archivedAt: raw.archivedAt ?? null,
  });
  const programBusinessModel = toProgramBusinessModelKind(
    raw.programBusinessModel,
  );
  const state = deriveProgramState({
    status,
    programBusinessModel: raw.programBusinessModel ?? null,
    offerState,
    programId: raw.id,
    bundles,
    creatorMode,
    isVisible: raw.isVisible ?? false,
  });
  return {
    id: raw.id,
    name: raw.name || '',
    status,
    state,
    mediaCover: toMediaCover(raw.mediaCover ?? null),
    subInfo: toSubInfo(raw),
    offerState,
    metrics: { ...metrics, rating: toRoundedRating(metrics.rating) },
    commentsCount: toCommentsCount(raw),
    salesType: toSalesType(raw.programBusinessModel, raw.accessType),
    index: raw.index ?? null,
    deeplinkUrl:
      raw.exclusiveProgramMetadata?.deeplinkUrl ?? raw.deeplinkUrl ?? null,
    programBusinessModel,
    notVisibleToConsumers: toNotVisibleToConsumers({
      status,
      programBusinessModel: raw.programBusinessModel ?? null,
      offerState,
      programId: raw.id,
      bundles,
      creatorMode,
      isVisible: raw.isVisible ?? false,
    }),
    hasChallenges: toHasChallenges(raw.challenges),
    hasOngoingChallenges: toHasOngoingChallenges(raw.challenges),
  };
};

const toProgramBundle = (b: RawBundle): ProgramBundle => ({
  id: b.id,
  title: b.title,
  isPlatformBundle: b.isPlatformBundle,
  status: toProgramBundleStatus(b.status),
});

/**
 * The creator GraphQL field is a plain string ('legacy' | 'bundle'); any
 * value other than the explicit `legacy` marker falls through to `new`.
 */
export const toCreatorMode = (
  businessModel: string | null | undefined,
): CreatorMode => (businessModel === 'legacy' ? 'legacy' : 'new');

const toCreator = (
  creator: RawCreator | null | undefined,
  fallbackId: number,
): ProgramsCreator => ({
  id: creator?.id ?? fallbackId,
  customURL: creator?.customURL ?? '',
  profileDeepLink: creator?.profileDeepLink ?? '',
  mode: toCreatorMode(creator?.businessModel),
});

/**
 * Top-level mapper: ProgramsGetListQuery → ProgramsDomain.
 *
 * Curried because the creator id is a caller-side input (comes from the
 * hook's `influencerId` param) rather than from the query itself.
 */
export const toProgramsDomain =
  ({ creatorId }: { creatorId: number }) =>
  (raw: ProgramsGetListQuery): ProgramsDomain => {
    const bundles = (raw.bundle || []).filter((b): b is RawBundle =>
      Boolean(b),
    );
    const collections = (raw.collections || []).filter((c): c is RawProgram =>
      Boolean(c),
    );
    const creator = toCreator(raw.creator, creatorId);
    return {
      creator,
      bundles: bundles.map(toProgramBundle),
      programs: collections.map((c) => toProgramItem(c, bundles, creator.mode)),
    };
  };
